// Services (api.ts) over a loaded world: state, actions, cached queries, live feeds.
//
//   createServices({ world, clients })
//        Store ── appState (snapshot, lensResult derived)          ► AppState
//          │  ▲
//          │  └── actions ◄── turns (changes + events → notifications), playback (autoplay)
//          └── worldService (queries at the store's date and leader) ► WorldService
//          └── feeds (interval + as-of trigger)                    ► FeedService
//
// Nothing here touches the DOM or fetches: the world and the feed clients come in.

import { effect } from '@preact/signals';
import { yearOf } from '../core/dates.ts';
import type { DateStr, LeaderId, WorldEvent } from '../core/schema.ts';
import type { FeedClients } from '../data/index.ts';
import type { WorldImpl } from '../world/index.ts';
import { createActions } from './actions.ts';
import type { Services } from './api.ts';
import { createFeeds } from './feeds.ts';
import { createPlayback } from './playback.ts';
import { appState, createStore, type Store } from './state.ts';
import { createTurns } from './turns.ts';
import { createWorldService, type WorldExtras } from './worldService.ts';

export { createBootServices, type BootServices } from './boot.ts';

/** The staleness window (365 days) reaches into the year before a date. */
const FOG_YEARS_BACK = 1;
const MONTH_YEAR_CHARS = 4;

export interface ServicesOptions {
  world: WorldImpl;
  clients: FeedClients;
  /** Wall clock in ms, for the feeds. */
  now?: () => number;
}

export interface ServicesHandle {
  services: Services & { world: Services['world'] & WorldExtras };
  /** Internals for the composition root and tests. */
  store: Store;
  /** Start the feed interval; returns a stop function. */
  startFeeds(): () => void;
}

export function createServices({ world, clients, now }: ServicesOptions): ServicesHandle {
  const store = createStore(world.asOf);
  const selfOf = (leader: LeaderId): string => world.perspectives.find((p) => p.id === leader)?.iso3 ?? '';
  const worldService = createWorldService(world, store);

  // Events years feed the fog of war: once one lands, snapshots derived before it are stale.
  async function events(from: DateStr, to: DateStr): Promise<WorldEvent[]> {
    const list = await world.events(from, to);
    store.dataVersion.value += 1;
    return list;
  }

  // Loads the years the fog at `date` needs, each once (a failed year may be asked for again).
  const eventYears = new Set(world.eventCounts().months.map(([month]) => Number(month.slice(0, MONTH_YEAR_CHARS))));
  const asked = new Set<number>();
  function loadFog(date: DateStr): void {
    const year = yearOf(date);

    for (let y = year - FOG_YEARS_BACK; y <= year; y++) {
      if (!eventYears.has(y) || asked.has(y)) {
        continue;
      }
      asked.add(y);
      events(`${y}-01-01`, `${y}-01-01`).catch(() => asked.delete(y));
    }
  }
  effect(() => {
    if (store.leader.value) {
      loadFog(store.date.value);
    }
  });

  const turns = createTurns(store, {
    changes: (from, to, leader) => world.changes(from, to, leader),
    events,
    snapshot: (date, leader) => world.snapshot(date, leader),
    actorIndex: (id) => world.actorIndex(id),
    source: (id) => world.source(id),
    selfOf,
  });

  let nextTurn = (): Promise<void> => Promise.resolve();
  const playback = createPlayback(store, () => nextTurn());
  const actions = createActions(
    store,
    {
      capitalHex: (leader) => world.countries.find((c) => c.iso3 === selfOf(leader))?.capital?.hex ?? null,
      countryAtHex: (hex) => worldService.countryAtHex(hex),
      hasLens: (id) => world.lensIds().some((l) => l.id === id),
    },
    turns,
    playback,
  );
  nextTurn = actions.nextTurn;

  const feeds = createFeeds({ clients, store, now });
  return {
    services: { state: appState(store, world), actions, world: worldService, feeds: feeds.service },
    store,
    startFeeds: feeds.start,
  };
}
